import { useState } from "react";
import { sendSlackMessageTracked } from "../../models/notifications";


export default function PartnersBodyComponent() {
    return (
        <div>
            <HeroSection />
            <WaysToPartner />
            <PartnerForm />
        </div>
    );
}


function HeroSection() {
    return (
        <div className="bg-gray-950 text-white py-24 px-6">
            <div className="max-w-4xl mx-auto">
                <div className="mb-4 font-mono text-blue-400 text-sm tracking-widest uppercase">
                    Work With Us
                </div>
                <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
                    Partner with <span style={{ color: "#D23C1E" }}>Extern Academy</span>
                </h1>
                <p className="text-xl text-gray-400 max-w-2xl leading-relaxed">
                    High school students build and ship real consumer products in 14 or 28 days,
                    guided by founders who have done it before. If your organization works with
                    young builders, supports the next generation of founders, or believes in
                    learning by shipping, there is a way for us to work together.
                </p>
            </div>
        </div>
    );
}


function WaysToPartner() {
    const tracks = [
        {
            label: "Startup Trainers",
            heading: "Lead a cohort as a Startup Trainer",
            body: "Founders who have raised capital and scaled a real product guide our teams through the full build cycle. Three standups a week, Slack availability throughout, and a letter of recommendation at the end. If you have shipped a product and want to pass on what you know, this is the role.",
            detail: "Requires: $1M+ raised, 100k+ users",
        },
        {
            label: "Sponsors",
            heading: "Sponsor a cohort or a student",
            body: "Cover the program fee for a student who could not otherwise participate, or sponsor an entire cohort under your brand. Sponsorships are acknowledged in the program and in alumni communications. We keep it simple and straightforward.",
            detail: "Partial and full cohort sponsorships available",
        },
        {
            label: "Schools and Programs",
            heading: "Bring Extern Academy to your students",
            body: "If you run a school, enrichment program, or student organization, we can work together to make Extern Academy accessible to your students. We have experience coordinating with academic calendars and have structured programs to fit around existing commitments.",
            detail: "Flexible scheduling, group placement support",
        },
        {
            label: "Organizations and Accelerators",
            heading: "Align your mission with ours",
            body: "If your organization supports young founders, underrepresented builders, or the next generation of tech talent, we want to hear from you. We are open to co-sponsorship, cross-promotion, and referral arrangements that serve students first.",
            detail: "Accelerators, nonprofits, community orgs",
        },
    ];

    return (
        <section className="py-20 px-6 bg-white">
            <div className="max-w-5xl mx-auto">
                <div className="mb-14">
                    <div className="mb-3 font-mono text-blue-500 text-sm tracking-widest uppercase">
                        Partnership Tracks
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
                        Four ways to get involved
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    {tracks.map((track) => (
                        <div
                            key={track.label}
                            className="border border-gray-200 rounded-xl p-8 flex flex-col gap-3"
                        >
                            <span className="text-xs font-semibold tracking-widest uppercase text-blue-500">
                                {track.label}
                            </span>
                            <h3 className="text-xl font-bold text-gray-900">{track.heading}</h3>
                            <p className="text-gray-600 leading-relaxed">{track.body}</p>
                            <p className="text-sm text-gray-400 mt-auto pt-4 border-t border-gray-100">
                                {track.detail}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}


function PartnerForm() {
    const [name, setName] = useState('');
    const [org, setOrg] = useState('');
    const [email, setEmail] = useState('');
    const [partnerType, setPartnerType] = useState('');
    const [message, setMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);

    function handleSubmit() {
        sendSlackMessageTracked(
            `New partner inquiry:\nName: ${name}\nOrg: ${org}\nEmail: ${email}\nType: ${partnerType}\nMessage: ${message}`,
            "#form-submissions"
        );
        if (typeof window.gtag === "function") {
            window.gtag("event", "form_submit", {
                form_type: "partner_inquiry",
                partner_type: partnerType,
                email: email,
            });
        }
        setSubmitted(true);
    }

    return (
        <div className="relative isolate bg-gray-50 px-6 py-24 sm:py-32 lg:px-8">
            <svg
                aria-hidden="true"
                className="absolute inset-0 -z-10 size-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)] stroke-gray-200"
            >
                <defs>
                    <pattern
                        x="50%"
                        y={-64}
                        id="partner-grid"
                        width={200}
                        height={200}
                        patternUnits="userSpaceOnUse"
                    >
                        <path d="M100 200V.5M.5 .5H200" fill="none" />
                    </pattern>
                </defs>
                <svg x="50%" y={-64} className="overflow-visible fill-gray-50">
                    <path
                        d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M299.5 800h201v201h-201Z"
                        strokeWidth={0}
                    />
                </svg>
                <rect fill="url(#partner-grid)" width="100%" height="100%" strokeWidth={0} />
            </svg>

            <div className="mx-auto max-w-xl lg:max-w-4xl">
                <h2 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                    Start the conversation.
                </h2>
                <p className="mt-3 text-lg text-gray-600">
                    Tell us who you are and how you want to get involved. We read every submission and follow up within a few business days.
                </p>

                {submitted ? (
                    <div className="mt-16 rounded-xl bg-white border border-gray-200 p-12 text-center">
                        <div className="text-3xl font-bold text-gray-900 mb-3">We got it.</div>
                        <p className="text-gray-500">Someone from our team will be in touch within a few business days.</p>
                    </div>
                ) : (
                    <div className="mt-16">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                            <div>
                                <label className="block font-semibold text-gray-900">Your name</label>
                                <div className="mt-2.5">
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="block ring w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block font-semibold text-gray-900">Organization</label>
                                <div className="mt-2.5">
                                    <input
                                        type="text"
                                        value={org}
                                        onChange={(e) => setOrg(e.target.value)}
                                        className="block ring w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block font-semibold text-gray-900">Email</label>
                                <div className="mt-2.5">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block ring w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block font-semibold text-gray-900">How do you want to partner?</label>
                                <div className="mt-2.5">
                                    <select
                                        value={partnerType}
                                        onChange={(e) => setPartnerType(e.target.value)}
                                        className="block ring w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                    >
                                        <option value="">Select one</option>
                                        <option value="startup_trainer">Startup Trainer</option>
                                        <option value="sponsor">Sponsor</option>
                                        <option value="school">School or Program</option>
                                        <option value="org">Organization or Accelerator</option>
                                        <option value="other">Something else</option>
                                    </select>
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block font-semibold text-gray-900">Tell us more</label>
                                <div className="mt-2.5">
                                    <textarea
                                        rows={5}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        className="block ring w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-10">
                            <button
                                onClick={handleSubmit}
                                className="block w-full rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            >
                                Send inquiry
                            </button>
                        </div>

                        <p className="mt-4 text-sm text-gray-500">
                            By submitting this form, I agree to the{' '}
                            <a href="/privacy" target="_blank" className="font-semibold text-indigo-600 whitespace-nowrap">
                                privacy policy
                            </a>.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
